<template>
  <div class="protocol">
    <div class="main-title">
      {{ appName }} Privacy Policy
    </div>
    <div>
      <br />
      Welcome to {{ appName }} (hereinafter referred to as the "Platform", "App"
      or "{{ appName }}"). This App is operated by {{ appName }} Team (hereinafter
      "we/us/our"). We attach paramount importance to your privacy and fully
      comply with EU GDPR, California CCPA/CPRA, U.S. COPPA and local data
      protection laws in Southeast Asia, Singapore PDPA, Australia APPs.
      <br /><br />
      This Privacy Policy describes how we collect, use, store, share and protect
      your personal information, as well as your legal data rights. By
      downloading, registering, logging in and using {{ appName }}, you fully
      acknowledge, read and agree to all terms of this Policy. If you disagree
      with any clauses, please stop using our services immediately.
      <br /><br />
      We may revise this Policy irregularly due to regulatory updates or business
      adjustments. Material revisions will be notified via prominent in-app
      pop-up announcements 15 days in advance. Your continuous use of
      {{ appName }} after the effective date of updates constitutes acceptance of
      the revised Privacy Policy.
      <br /><br />
    </div>

    <div class="bt">1. Information We Collect</div>
    <div>
      We only collect information necessary to deliver core social &amp; chat
      functions, divided into three categories:
      <br /><br />
    </div>
    <div class="bt subtitle">1.1 Information You Voluntarily Provide</div>
    <div>
      1. Account registration data: Nickname, gender, date of birth, country/region,
      age, email address, login password, avatar, self-set account tags, profile
      signature;
      <br /><br />
      2. Personal homepage profile data: Photos, public gender and personal display
      content visible to other {{ appName }} users;
      <br /><br />
      3. UGC User-Generated Content: Chat texts, private messages, comments,
      uploaded pictures, images, short videos, audio voice recordings, streaming
      content, captions and hashtags you post;
      <br /><br />
      4. Payment &amp; transaction data: PayPal, Google Pay, Apple Pay payment
      account information, transaction serial numbers, purchase records of
      platform virtual coins. We will not store complete bank card or credit card
      billing details;
      <br /><br />
      5. Feedback &amp; verification information: Content submitted for account
      verification, customer service consultation, complaint submission, and
      answers for in-app official activities/surveys.
      <br /><br />
    </div>
    <div class="bt subtitle">1.2 Information Obtained From Third-Party Sources</div>
    <div>
      1. Third-party social login data: If you log in via Facebook, Google or Apple
      ID, we obtain authorized public profile including avatar, nickname and social
      contact list;
      <br /><br />
      2. Third-party cooperative data: Demographic, age and regional statistics
      from authorized advertising networks, data analytics service providers and
      public legal channels.
      <br /><br />
    </div>
    <div class="bt subtitle">1.3 Automatically Collected Device &amp; Behavior Information</div>
    <div>
      1. Device basic data: IP address, unique device identifier (OAID/IDFA),
      device model, screen resolution, OS version, App version, mobile carrier,
      time zone, file format information;
      <br /><br />
      2. Location data: Approximate city-level location via IP address; precise
      GPS location only after your explicit authorization, only city data will be
      uploaded to servers;
      <br /><br />
      3. Message metadata: Chat send/receive time, read status, communication
      object ID, message content metadata;
      <br /><br />
      4. Upload content metadata: Creation time, author ID, format, subtitles,
      hashtags attached to your videos and photos;
      <br /><br />
      5. Purchase log data: Virtual coin purchase time, channel and amount through
      Google Play &amp; official payment channels;
      <br /><br />
      6. App operation logs: Page dwell time, click behavior, crash logs, function
      usage frequency, Cookies and tracking identifiers.
      <br /><br />
    </div>

    <div class="bt">2. Legal Basis &amp; Purposes of Using Your Personal Information</div>
    <div>
      We process your data based on four legal grounds: performance of service
      contract, user explicit consent, legitimate business interests, and legal
      compliance obligations. All data use is limited to the following clear
      purposes:
      <br /><br />
      1. Account management &amp; core service delivery: Complete registration,
      login verification, real-time chat, friend matching, content publishing,
      social interaction and message delivery;
      <br /><br />
      2. Platform operation &amp; troubleshooting: Maintain daily App stability,
      repair bugs, collect crash logs, conduct data testing and statistical
      analysis, optimize overall product experience;
      <br /><br />
      3. Personalized content recommendation: Push region-adapted feeds, recommend
      chat partners and content matching your usage preferences;
      <br /><br />
      4. Security risk control &amp; anti-fraud: Detect abnormal login, account
      theft, spam and illegal content, block abusive accounts and protect user
      asset safety;
      <br /><br />
      5. Age compliance verification: Confirm you meet the minimum age requirement
      to use {{ appName }} per global privacy laws;
      <br /><br />
      6. Official notification delivery: Send system updates, service adjustments,
      event promotions and security alerts; you may opt out of marketing push
      notifications anytime in device settings;
      <br /><br />
      7. Activity operation: Verify participation eligibility, lottery winning
      confirmation and prize distribution for official competitions and
      promotions;
      <br /><br />
      8. Algorithm optimization: Provide anonymous user behavior data to improve
      matching and chat interaction algorithms;
      <br /><br />
      9. Legal compliance: Respond to law enforcement summons, court orders and
      regulatory investigations, retain transaction records for legal tax retention
      requirements;
      <br /><br />
      10. Integrated legitimate use: Combine anonymized data for market research
      without identifying individual users.
      <br /><br />
    </div>

    <div class="bt">3. Sharing &amp; Disclosure of Personal Information</div>
    <div>
      We will never sell, rent or trade your personal information for commercial
      profit. We only share limited necessary data in the following scenarios:
      <br /><br />
    </div>
    <div class="bt subtitle">3.1 Authorized Third-Party Service Providers</div>
    <div>
      We cooperate with regulated vendors to support App operation, all of whom
      sign strict data processing agreements and must comply with global privacy
      laws:
      <br /><br />
      - Instant messaging SDK: Huanxin IM SDK (support real-time chat function)
      <br /><br />
      - Cloud storage: Tencent cloud server (store user UGC and chat logs)
      <br /><br />
      - Data analytics &amp; crash monitoring: Firebase Analytics, Crashlytics
      <br /><br />
      - Advertising service: Google AdMob, Meta Audience Network (only advertising
      ID for personalized ads, support opt-out)
      <br /><br />
      - Payment processing: Google Pay, Apple Pay, PayPal official payment channels
      <br /><br />
      - Message push: Firebase Cloud Messaging
      <br /><br />
    </div>
    <div class="bt subtitle">3.2 With Your Explicit Active Consent</div>
    <div>
      When you actively share {{ appName }} content to third-party social
      platforms, or authorize cross-platform account data synchronization, we will
      share your selected content with designated third parties.
      <br /><br />
    </div>
    <div class="bt subtitle">3.3 Mandatory Legal Disclosure</div>
    <div>
      We may disclose your personal data when required by judicial warrants, court
      judgments, government regulatory inquiries, or to prevent illegal acts,
      protect the legitimate rights of {{ appName }}, other users and the public.
      <br /><br />
    </div>
    <div class="bt subtitle">3.4 Enterprise Merger, Acquisition or Asset Transfer</div>
    <div>
      If {{ appName }} undergoes merger, acquisition or asset transfer, we will
      notify users in advance and require the transferee to bear equivalent data
      protection obligations. If you disagree with the data transfer, you may
      apply for full account deletion before the transaction completes.
      <br /><br />
    </div>
    <div class="bt subtitle">3.5 Public Visible UGC</div>
    <div>
      When you post public comments, videos and profile information on
      {{ appName }}, other platform users may view, collect and save such content.
      Please carefully evaluate personal privacy disclosure before publishing
      public content; we are not liable for third-party unauthorized saving or
      dissemination of your voluntarily public content.
      <br /><br />
    </div>

    <div class="bt">4. Your Privacy Rights</div>
    <div class="bt subtitle">4.1 Universal Global User Rights</div>
    <div>
      1. Cookie &amp; tracking opt-out: Disable Cookies via browser or App system
      settings; disabling tracking may affect partial chat and login functions;
      opt-out only takes effect on your current device;
      <br /><br />
      2. Access &amp; correction: Log into your {{ appName }} account to view,
      edit and update your profile, nickname, avatar and personal information
      anytime;
      <br /><br />
      3. Account &amp; full data deletion: Send identity-verified deletion requests
      to official email {{ appEmail }}. We require supporting materials including
      account ID, recent transaction records and device verification code to
      confirm ownership. We complete full data erasure within 72 hours after
      verification and issue an official deletion confirmation via email;
      <br /><br />
      4. Right to withdraw consent: Revoke all device permission authorizations
      (camera, mic, location, storage) in system settings at any time;
      <br /><br />
      5. Right to restrict processing &amp; data portability: Request us to suspend
      data processing or export your personal data in machine-readable format for
      transfer to other platforms;
      <br /><br />
      6. Right to object personalized advertising: Disable targeted ad tracking in
      App privacy settings or mobile system ad ID reset function.
      <br /><br />
    </div>
    <div class="bt subtitle">4.2 EU/EEA &amp; UK Users (GDPR Additional Rights)</div>
    <div>
      You have the right to submit formal complaints to your local data protection
      authority if you believe we violate GDPR data processing rules. All data
      cross-border transmission from EU regions adopts EU Standard Contractual
      Clauses (SCCs) to guarantee equivalent data security standards.
      <br /><br />
    </div>
    <div class="bt subtitle">4.3 California Residents (CCPA/CPRA Rights)</div>
    <div>
      1. Right to know: Request full categories, usage and sharing records of all
      personal data we collect about you;
      <br /><br />
      2. Right to opt-out of data sharing: We do not sell user data; you may turn
      off cross-platform ad sharing via the dedicated "Do Not Sell My Data" entry
      in {{ appName }} Settings;
      <br /><br />
      3. Non-discrimination guarantee: We will not restrict core chat service
      functions or charge differential fees if you exercise any privacy rights.
      <br /><br />
    </div>

    <div class="bt">5. Data Security Protection</div>
    <div>
      We adopt multi-layer administrative, technical and physical security
      safeguards to prevent unauthorized access, leakage, tampering and loss of
      personal information:
      <br /><br />
      1. Transmission encryption: All App data transmission via TLS/HTTPS encrypted
      channels;
      <br /><br />
      2. Static storage encryption: Sensitive data (password, payment records)
      protected by AES-256 encryption on cloud servers;
      <br /><br />
      3. Staff access control: Role-based permission management; internal employees
      can only access user data within job necessity scope with strict audit logs;
      <br /><br />
      4. Real-time security monitoring: Regular vulnerability scanning, risk
      interception for abnormal login and mass data export behavior;
      <br /><br />
      5. Data breach notification: If personal information leakage occurs, we will
      notify affected users and competent regulatory authorities within 72 hours
      as required by GDPR.
      <br /><br />
      Note: Internet transmission has inherent security risks. We cannot fully
      eliminate all network data transmission risks, please submit sensitive
      personal information with caution.
      <br /><br />
    </div>

    <div class="bt">6. Data Retention Rules</div>
    <div>
      1. Normal service period: Retain your account data, chat logs and UGC to
      support continuous use of {{ appName }};
      <br /><br />
      2. After account deletion: Within 30 days of deletion approval, we delete or
      fully anonymize all identifiable personal data;
      <br /><br />
      3. Legal retention exception: Transaction, tax and judicial-related records
      will be retained for the statutory required period (5–7 years);
      <br /><br />
      4. Anonymous statistical data: Desensitized aggregate data without user
      identifiers can be retained indefinitely and cannot be linked to any specific
      individual user.
      <br /><br />
    </div>

    <div class="bt">7. Minor Protection (COPPA + GDPR Minor Rules, Zero Tolerance for CSAM)</div>
    <div class="bt subtitle">7.1 Age Restriction</div>
    <div>
      {{ appName }} services are not intended for users under 18 years old. We will
      not knowingly collect personal information from minors without verifiable
      parental/guardian consent. If you are a parent or guardian and discover your
      minor child has registered a {{ appName }} account and submitted personal
      data, contact us immediately via {{ appEmail }}. We will stop all data
      processing and erase all minor-related information as soon as verification
      completes.
      <br /><br />
    </div>
    <div class="bt subtitle">7.2 Prohibited Child Harm Content (Zero Tolerance)</div>
    <div>
      Users are strictly forbidden to upload, send or disseminate any Child Sexual
      Abuse Material (CSAM) and minor-harm content, including but not limited to:
      <br /><br />
      1. Grooming minors for offline sexual contact or pornographic exchange;
      <br /><br />
      2. Sexualized depiction, sexual extortion and blackmail of minors via private
      photos;
      <br /><br />
      3. Language, audio or video content involving inappropriate physical contact
      with children;
      <br /><br />
      4. Child trafficking, commercial sexual exploitation promotion;
      <br /><br />
      5. Violent, dangerous, cosmetic weight-loss/surgery entertainment content
      targeting minors that distorts self-image.
      <br /><br />
      Once we detect CSAM or minor-abuse content, we will immediately delete
      content, permanently ban the account, disable the device and cooperate fully
      with law enforcement agencies by disclosing relevant chat and account data.
      <br /><br />
    </div>
    <div class="bt subtitle">7.3 Minor Data Processing Restrictions</div>
    <div>
      If we accidentally collect minor information:
      <br /><br />
      1. Limited collection scope: Only username, password, birthday, device ID, IP,
      country-level location and App activity logs;
      <br /><br />
      2. Strict usage limits: Only used for identity verification, security risk
      prevention and compliance audit; never used for commercial advertising,
      marketing or third-party commercial sharing;
      <br /><br />
      3. No sale of minor data: We will never disclose minor personal information
      to advertisers or commercial third parties;
      <br /><br />
      4. Guardian rights: Guardians may apply to access, check or delete minor
      account data via official email without discriminatory service treatment.
      <br /><br />
    </div>
    <div class="bt subtitle">7.4 Minor Content Reporting Channel</div>
    <div>
      If you find any content endangering minors on {{ appName }}, submit a report
      to {{ appEmail }}, we will complete investigation and disposal within 48
      hours.
      <br /><br />
    </div>

    <div class="bt">8. Device Permissions &amp; Third-Party SDK Detailed Statement</div>
    <div class="bt subtitle">8.1 Device Permission Description</div>
    <div>
      We only apply for permissions required to realize core chat functions; you can
      enable or disable all permissions independently in your mobile system
      settings:
      <br /><br />
    </div>
    <div class="table-container">
      <table>
        <tr>
          <td>Permission Category</td>
          <td>Android Permission Code</td>
          <td>Function &amp; Usage Scenario</td>
        </tr>
        <tr>
          <td>Network State</td>
          <td>ACCESS_NETWORK_STATE, ACCESS_WIFI_STATE</td>
          <td>Detect network connection to stabilize chat and video call transmission</td>
        </tr>
        <tr>
          <td>Internet Access</td>
          <td>INTERNET</td>
          <td>Real-time message synchronization, cloud upload/download of UGC</td>
        </tr>
        <tr>
          <td>Bluetooth</td>
          <td>BLUETOOTH, BLUETOOTH_CONNECT</td>
          <td>Support Bluetooth earphone voice chat audio transmission</td>
        </tr>
        <tr>
          <td>Audio Recording</td>
          <td>RECORD_AUDIO, MODIFY_AUDIO_SETTINGS</td>
          <td>Voice message, voice call, video chat audio capture</td>
        </tr>
        <tr>
          <td>Camera</td>
          <td>CAMERA</td>
          <td>Take profile avatar, video chat, upload photo/video content</td>
        </tr>
        <tr>
          <td>Storage Read</td>
          <td>READ_EXTERNAL_STORAGE, READ_MEDIA_*</td>
          <td>Read local photos, videos, audio for uploading to chats</td>
        </tr>
        <tr>
          <td>Storage Write</td>
          <td>WRITE_EXTERNAL_STORAGE</td>
          <td>Save chat media, cache files locally for offline viewing</td>
        </tr>
        <tr>
          <td>Push Notification</td>
          <td>POST_NOTIFICATIONS, RECEIVE_PUSH</td>
          <td>Send chat message alerts, system and activity notifications</td>
        </tr>
      </table>
    </div>
    <br /><br />
    <div class="bt subtitle">8.2 Third-Party SDK List &amp; Compliance</div>
    <div>
      {{ appName }} integrates the following third-party SDKs to deliver complete
      social chat functions; all SDKs process data per their own privacy policies,
      and we conduct regular compliance audits:
      <br /><br />
      1. Huanxin Instant Messaging SDK: Real-time private/group chat, voice/video call
      support;
      <br /><br />
      2. Firebase SDK (Analytics &amp; Crashlytics): Anonymous usage statistics, App
      crash troubleshooting;
      <br /><br />
      3. Google AdMob SDK: Personalized advertising push, support user opt-out;
      <br /><br />
      4. Payment SDKs (Google Pay/Apple Pay/PayPal): Complete virtual coin
      transaction settlement, no storage of complete financial card data.
      <br /><br />
    </div>

    <div class="bt">9. Cookie &amp; Tracking Technology</div>
    <div>
      {{ appName }} App and official website use Cookies, SDK tracking identifiers
      and pixel tags to record user behavior for function optimization and
      advertising matching. At the first launch of {{ appName }}, a pop-up consent
      window will distinguish between necessary service tracking and optional
      marketing tracking; you may reject non-essential tracking at any time via
      device settings. Rejecting necessary Cookies may cause login failure and
      abnormal chat functions.
      <br /><br />
    </div>

    <div class="bt">10. Policy Revision &amp; Notification</div>
    <div>
      We reserve the right to update this Privacy Policy to adapt to new laws and
      business adjustments. For core changes involving data collection scope, user
      rights or minor protection, we will issue prominent in-app announcements 15
      days in advance. Continued use of {{ appName }} after the update effective
      date means you accept the revised Policy. If you disagree with revised
      clauses, you must stop all use of {{ appName }} services.
      <br /><br />
    </div>

    <div class="bt">11. Contact Information</div>
    <div>
      Data Controller: {{ appName }} Team
      <br /><br />
      Official Support Email: {{ appEmail }}
      <br /><br />
      Server Storage Location: Tencent Cloud
      <br /><br />
      If you have inquiries, complaints or data rights requests regarding this
      Privacy Policy, personal information processing or minor protection, please
      send emails to the above mailbox with your {{ appName }} account ID for
      identity verification. We will review and respond to all valid requests
      within 30 calendar days.
      <br /><br />
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {};
  },
  created() {},
};
</script>

<style scoped>
.table-container {
  width: 100%;
  overflow-x: auto;
}
table {
  border-collapse: collapse;
  max-width: 100%;
  width: 100%;
  margin: 0.2267rem 0;
  font-family: Arial, sans-serif;
}
td {
  padding: 0.2267rem;
  border: 1px solid #ddd;
  word-wrap: break-word;
  white-space: normal;
}

.protocol {
  width: 100%;
  font-size: 0.9rem;
  margin-bottom: 0.4667rem;
  color: #666;
  padding-left: 0.4rem;
  padding-right: 0.4rem;
  box-sizing: border-box;
}
.main-title {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.4;
  margin: 0.5333rem 0 0.8rem;
  color: #000;
  text-align: center;
}
.bt {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0.5333rem 0px;
  color: #000;
}
.subtitle {
  font-size: 1.1rem;
  color: #333;
}
a {
  color: #409eff;
  word-break: break-all;
}
</style>
